// deps
import React, { ReactElement, useContext, memo, useMemo, useCallback } from "react";

// internal deps
import { PaymentAlert, PaymentMethod, PaymentMethodMeta } from "@swiggy-private/payment-ui";
import { format } from "@swiggy-private/core-ui";
import { InvalidCouponAction, OnPayExtraParams, PaymentAlertActionProps } from "@swiggy-private/payment-types";
import { CheckoutContext, CheckoutActionTypes } from "@swiggy-private/checkout-core";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

import * as TextDict from "@payment-client/constants/TextDict";

// helpers
import { getPaymentFailedAlertSubInfo as getInvalidCouponAlertSubInfo } from "@payment-client/helpers/PaymentHelper";
import { InvalidCouponAlertPayload } from "@payment-client/interfaces/Checkout";
import { TEXTS, ACTION_TYPES } from "@payment-client/constants/InvalidCouponAlertData";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

interface PaymentFailedAlertProps {
    payAction: (paymentMethodData: PaymentMethod<PaymentMethodMeta>, _extraParams?: OnPayExtraParams) => void;
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"];
    amountToPay: string;
    itemQuantity: number;
    payload: InvalidCouponAlertPayload;
    amountToPayWithoutCouponWithoutSplitpay: string;
}

function InvalidCouponAlert(props: PaymentFailedAlertProps): ReactElement | null {
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { paymentResponse } = useContext(ShowPaymentContext);

    const { invalidCouponResponseData, placeOrderData } = props.payload;
    const { title, actionTypes } = TEXTS;
    const { couponErrorMessage } = invalidCouponResponseData;
    const info = format(TextDict.PAYMENT_ALERT_MAIN_TEXT, props.amountToPay);
    const { payAction } = props;

    const subInfo = useMemo(() => getInvalidCouponAlertSubInfo(props.itemQuantity), [props.itemQuantity]);

    const handleActionClick = useCallback(
        (method: InvalidCouponAction): void => {
            switch (method.actionType) {
                case ACTION_TYPES.PROCEED_WITHOUT_COUPON: {
                    if (!placeOrderData.extraParams) {
                        placeOrderData.extraParams = {};
                    }
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    placeOrderData.extraParams.force_validate_coupon = false;
                    placeOrderData.extraParams.transaction_amount = paymentResponse?.paymentAmountWithoutDiscount;
                    placeOrderData.extraParams.paymentAmountWithoutDiscount =
                        paymentResponse?.paymentAmountWithoutDiscount;
                    payAction(placeOrderData.paymentMethodData, placeOrderData.extraParams);
                    break;
                }
                case ACTION_TYPES.DISMISS:
                    setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
                    break;
            }
        },
        [setCheckoutState, placeOrderData, paymentResponse, payAction],
    );

    const onOverlayClose = useCallback(() => {
        setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
    }, [setCheckoutState]);

    const actionItems: PaymentAlertActionProps[] = useMemo(
        () =>
            actionTypes.map(action => {
                return {
                    text: action.displayName,
                    subText:
                        action.actionType === ACTION_TYPES.PROCEED_WITHOUT_COUPON
                            ? format(TextDict.X_WILL_BE_CHARGED, props.amountToPayWithoutCouponWithoutSplitpay)
                            : undefined,
                    onClick: (): void => {
                        handleActionClick(action);
                    },
                };
            }),
        [handleActionClick, actionTypes, props.amountToPayWithoutCouponWithoutSplitpay],
    );

    if (checkoutState.type !== CheckoutActionTypes.INVALID_COUPON_ALERT) {
        return null;
    }

    return (
        <PaymentAlert
            icon={TextDict.ICON_PAYMENT_FAILED_V4}
            title={title}
            message={couponErrorMessage}
            info={info}
            subInfo={subInfo}
            primaryAction={actionItems[0]}
            secondaryAction={actionItems[1]}
            hideOnOverlayClick={true}
            onClose={onOverlayClose}
        />
    );
}

export default memo(InvalidCouponAlert);
